import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

export default function MasterInviteCodeManager() {
  const [masters, setMasters] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingCode, setCreatingCode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [expiresIn, setExpiresIn] = useState(30);
  const [maxUses, setMaxUses] = useState(1);
  const [codeToReveal, setCodToReveal] = useState(null);
  const [copiedCodeId, setCopiedCodeId] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  useEffect(() => {
    loadMastersAndCodes();
  }, []);

  const loadMastersAndCodes = async () => {
    try {
      setLoading(true);
      
      const [mastersRes, codesRes] = await Promise.all([
        fetch('/api/master/admin/masters', {
          credentials: 'include'
        }),
        fetch('/api/master/admin/masters/invite-codes', {
          credentials: 'include'
        })
      ]);

      if (!mastersRes.ok || !codesRes.ok) {
        throw new Error('Failed to load data');
      }

      const mastersData = await mastersRes.json();
      const codesData = await codesRes.json();

      setMasters(mastersData.data || []);
      setInviteCodes(codesData.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load Masters and invite codes');
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCode = async (e) => {
    e.preventDefault();
    
    if (!selectedMaster) {
      toast.error('Please select a Master User');
      return;
    }

    setCreatingCode(true);
    try {
      const response = await fetch(`/api/master/admin/masters/${selectedMaster}/invite-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          max_uses: maxUses,
          days_to_expire: expiresIn
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate code');
      }

      const data = await response.json();
      
      setInviteCodes([...inviteCodes, data.data]);
      toast.success('Invite code generated successfully!');
      setShowForm(false);
      setSelectedMaster('');
      setExpiresIn(30);
      setMaxUses(1);
    } catch (error) {
      console.error('Error generating code:', error);
      toast.error(error.message || 'Failed to generate invite code');
    } finally {
      setCreatingCode(false);
    }
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCodeId(id);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopiedCodeId(null), 2000);
  };

  const revokeCode = async (codeId) => {
    try {
      const response = await fetch(`/api/master/admin/masters/invite-codes/${codeId}/revoke`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to revoke code');
      }

      setInviteCodes(inviteCodes.filter(code => code.id !== codeId));
      toast.success('Invite code revoked');
    } catch (error) {
      console.error('Error revoking code:', error);
      toast.error('Failed to revoke invite code');
    }
  };

  const getMasterName = (masterUserId) => {
    const master = masters.find(m => m.id === masterUserId);
    return master?.company_name || master?.email || 'Unknown Master';
  };

  const isCodeExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const getCodeStatus = (code) => {
    if (code.status === 'used') return 'Used';
    if (isCodeExpired(code.expires_at)) return 'Expired';
    if (code.current_uses >= code.max_uses) return 'Exhausted';
    return 'Active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'text-green-600 bg-green-50';
      case 'Used':
        return 'text-blue-600 bg-blue-50';
      case 'Expired':
        return 'text-red-600 bg-red-50';
      case 'Exhausted':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'Expired':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <SparklesIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master User Invitations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Generate and manage invitation codes for Master Users to sign up
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Generate Code
        </button>
      </div>

      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate New Invite Code</h2>
          <form onSubmit={generateInviteCode} className="space-y-4">
            <div>
              <label htmlFor="master" className="block text-sm font-medium text-gray-700">
                Select Master User
              </label>
              <select
                id="master"
                value={selectedMaster}
                onChange={(e) => setSelectedMaster(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border px-3 py-2"
              >
                <option value="">Choose a Master User...</option>
                {masters.map(master => (
                  <option key={master.id} value={master.id}>
                    {master.company_name} ({master.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiresIn" className="block text-sm font-medium text-gray-700">
                  Expires In (Days)
                </label>
                <input
                  type="number"
                  id="expiresIn"
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(parseInt(e.target.value) || 30)}
                  min="1"
                  max="365"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="maxUses" className="block text-sm font-medium text-gray-700">
                  Max Uses
                </label>
                <input
                  type="number"
                  id="maxUses"
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
                  min="1"
                  max="10"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border px-3 py-2"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={creatingCode}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
              >
                {creatingCode ? 'Generating...' : 'Generate Code'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Invite Codes ({inviteCodes.length})
          </h3>
        </div>

        {inviteCodes.length === 0 ? (
          <div className="border-t border-gray-200 px-4 py-12 text-center">
            <SparklesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invite codes</h3>
            <p className="mt-1 text-sm text-gray-500">Create the first one to get started</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {inviteCodes.map((code) => {
              const status = getCodeStatus(code);
              const statusColor = getStatusColor(status);
              const statusIcon = getStatusIcon(status);

              return (
                <li key={code.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">
                          {codeToReveal === code.id ? code.code : '●'.repeat(15)}
                        </code>
                        <button
                          onClick={() => setCodToReveal(codeToReveal === code.id ? null : code.id)}
                          className="text-gray-500 hover:text-gray-700"
                          title={codeToReveal === code.id ? 'Hide' : 'Show'}
                        >
                          {codeToReveal === code.id ? (
                            <EyeSlashIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => copyToClipboard(code.code, code.id)}
                          className="text-gray-500 hover:text-blue-600"
                          title="Copy to clipboard"
                        >
                          {copiedCodeId === code.id ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Master User</p>
                          <p className="text-gray-900 font-medium">{getMasterName(code.master_user_id)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Status</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1 ${statusColor}`}>
                            {statusIcon}
                            {status}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-500">Uses</p>
                          <p className="text-gray-900">{code.current_uses} / {code.max_uses}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Expires</p>
                          <p className="text-gray-900">
                            {new Date(code.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setConfirmRevoke(code.id)}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ How It Works</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Generate a unique invite code for each Master User</li>
          <li>• Share the code with the Master User (via email, etc.)</li>
          <li>• Master User visits /master/signup and enters the code</li>
          <li>• They create their account and can start using the portal</li>
          <li>• Codes expire after the specified number of days</li>
        </ul>
      </div>

      {confirmRevoke && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Revoke Invite Code?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to revoke this invite code? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmRevoke(null)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await revokeCode(confirmRevoke);
                  setConfirmRevoke(null);
                }}
                className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md font-medium"
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
